import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import TenantUserService, { TenantUserFormPayload } from '../../../services/tenant-user.service';
import TenantService from '../../../services/tenant.service';
import { usePermission } from '../../../hooks/usePermission';
import { Modules, AuthRoutes, NonAuthRoutes } from '../../../enums';

interface TenantOption {
  id: number;
  tenant_name: string;
}

interface TenantUserFormState {
  tenant_id: number | '';
  name: string;
  email: string;
  phone: string;
  role: 'owner' | 'admin' | 'member' | '';
  status: string;
  notes: string;
}

const initialForm: TenantUserFormState = {
  tenant_id: '',
  name: '',
  email: '',
  phone: '',
  role: '',
  status: 'invited',
  notes: '',
};

const TenantUserForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const canManageUsers = usePermission('user:invite');
  const edit = !!id;
  const lockedTenantId = searchParams.get('tenantId');

  const [formData, setFormData] = useState<TenantUserFormState>(
    lockedTenantId ? { ...initialForm, tenant_id: Number(lockedTenantId) } : initialForm
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  useEffect(() => {
    TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setTenants(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    setFetching(true);
    TenantUserService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          tenant_id: row.tenant_id ?? '',
          name: row.name ?? '',
          email: row.email ?? '',
          phone: row.phone ?? '',
          role: row.role ?? '',
          status: row.status ?? 'invited',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const tenantLocked = !edit && !!lockedTenantId;

  const validate = useCallback(
    (data: TenantUserFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.tenant_id) nextErrors.tenant_id = t('tenantUsers.form.pageTexts.fields.tenant.errors.required');
      if (!data.name.trim()) nextErrors.name = t('tenantUsers.form.pageTexts.fields.name.errors.required');
      if (!data.email.trim()) nextErrors.email = t('tenantUsers.form.pageTexts.fields.email.errors.required');
      if (!data.role) nextErrors.role = t('tenantUsers.form.pageTexts.fields.role.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        if (edit && id) {
          const payload: Partial<Omit<TenantUserFormPayload, 'tenant_id'>> = {
            name: formData.name,
            phone: formData.phone || undefined,
            role: formData.role as 'owner' | 'admin' | 'member',
            notes: formData.notes || undefined,
          };
          await TenantUserService.update(id, payload);
          toast.success(<Toast message={t('tenantUsers.form.pageTexts.updateSuccess')} />, { icon: false });
        } else {
          const payload: TenantUserFormPayload = {
            tenant_id: Number(formData.tenant_id),
            name: formData.name,
            email: formData.email,
            phone: formData.phone || undefined,
            role: formData.role as 'owner' | 'admin' | 'member',
            notes: formData.notes || undefined,
          };
          await TenantUserService.create(payload);
          toast.success(<Toast message={t('tenantUsers.form.pageTexts.inviteSuccess', { email: formData.email })} />, { icon: false });
        }
        if (lockedTenantId) {
          navigate(`${Modules.tenants}/${lockedTenantId}`);
        } else {
          navigate(`${Modules.iam}${AuthRoutes.tenantUsers}`);
        }
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, lockedTenantId, navigate, validate, t]
  );

  const cancelTarget = useMemo(
    () => (lockedTenantId ? `${Modules.tenants}/${lockedTenantId}` : `${Modules.iam}${AuthRoutes.tenantUsers}`),
    [lockedTenantId]
  );

  if (!canManageUsers) {
    return <Navigate replace to={{ pathname: Modules.error + NonAuthRoutes.forbidden }} />;
  }

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('tenantUsers.form.pageTexts.editTitle') : t('tenantUsers.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="name"
            name="name"
            label={t('tenantUsers.form.pageTexts.fields.name.label')}
            placeholder={t('tenantUsers.form.pageTexts.fields.name.placeholder')}
            required
            value={formData.name}
            error={!!errors.name}
            errorMessage={errors.name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="email"
            name="email"
            type="email"
            label={t('tenantUsers.form.pageTexts.fields.email.label')}
            placeholder={t('tenantUsers.form.pageTexts.fields.email.placeholder')}
            required
            disabled={edit}
            value={formData.email}
            error={!!errors.email}
            errorMessage={errors.email}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="phone"
            name="phone"
            label={t('tenantUsers.form.pageTexts.fields.phone.label')}
            placeholder={t('tenantUsers.form.pageTexts.fields.phone.placeholder')}
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="tenant_id">
            {t('tenantUsers.form.pageTexts.fields.tenant.label')}
          </label>
          <select
            id="tenant_id"
            name="tenant_id"
            className={`form-select ${errors.tenant_id ? 'is-invalid' : ''}`}
            value={formData.tenant_id}
            disabled={edit || tenantLocked}
            onChange={(e) => handleChange(e, 'tenant_id')}
          >
            <option value="">{t('tenantUsers.form.pageTexts.fields.tenant.placeholder')}</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.tenant_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="role">
            {t('tenantUsers.form.pageTexts.fields.role.label')}
          </label>
          <select
            id="role"
            name="role"
            className={`form-select ${errors.role ? 'is-invalid' : ''}`}
            value={formData.role}
            onChange={(e) => handleChange(e, 'role')}
          >
            <option value="">{t('tenantUsers.form.pageTexts.fields.role.placeholder')}</option>
            <option value="owner">{t('tenantUsers.form.pageTexts.fields.role.options.owner')}</option>
            <option value="admin">{t('tenantUsers.form.pageTexts.fields.role.options.admin')}</option>
            <option value="member">{t('tenantUsers.form.pageTexts.fields.role.options.member')}</option>
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block">{t('tenantUsers.form.pageTexts.fields.status.label')}</label>
          <span className="badge bg-label-warning">
            {edit ? formData.status : t('tenantUsers.form.pageTexts.fields.status.value')}
          </span>
        </div>

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('tenantUsers.form.pageTexts.fields.notes.label')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('tenantUsers.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(cancelTarget)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={edit ? t('tenantUsers.form.pageTexts.buttons.save') : t('tenantUsers.form.pageTexts.buttons.invite')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default TenantUserForm;
